/**
 * 12-hour AM/PM time picker with quick-pick slot grid.
 * Value/onChange use 24-hour "HH:mm" format for backend compatibility.
 * Pass minDate (YYYY-MM-DD) to disable past time slots when today is selected.
 */
import React, { useMemo, useState } from 'react';
import { Clock, ChevronDown } from 'lucide-react';
import styles from './TimePicker12h.module.css';

const SLOTS = [];
for (let h = 9; h <= 20; h++) {
  for (const m of ['00', '15', '30', '45']) {
    if (h === 20 && m !== '00') break;
    const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
    const ap = h < 12 ? 'AM' : 'PM';
    SLOTS.push({
      label: `${h12}:${m} ${ap}`,
      value: `${h.toString().padStart(2, '0')}:${m}`,
    });
  }
}

const format12h = (hhmm) => {
  if (!hhmm || !/^\d{1,2}:\d{2}$/.test(hhmm)) return '';
  const [h, m] = hhmm.split(':').map(Number);
  const min = m.toString().padStart(2, '0');
  if (h === 0) return `12:${min} AM`;
  if (h === 12) return `12:${min} PM`;
  if (h < 12) return `${h}:${min} AM`;
  return `${h - 12}:${min} PM`;
};

const isToday = (dateStr) => {
  if (!dateStr) return false;
  return dateStr === new Date().toISOString().split('T')[0];
};

const TimePicker12h = ({ value, onChange, disabled, className = '', minDate }) => {
  const [open, setOpen] = useState(false);
  const todaySelected = isToday(minDate);
  const nowMinutes = useMemo(() => {
    const d = new Date();
    return d.getHours() * 60 + d.getMinutes();
  }, []);

  const isSlotDisabled = (slotValue) => {
    if (!todaySelected) return false;
    const [h, m] = slotValue.split(':').map(Number);
    return h * 60 + m <= nowMinutes;
  };

  const handleSelect = (slotValue) => {
    onChange(slotValue);
    setOpen(false);
  };

  const displayValue = value ? format12h(value) : '';

  return (
    <div className={`${styles.wrapper} ${className}`}>
      <button
        type="button"
        className={styles.trigger}
        onClick={() => !disabled && setOpen((p) => !p)}
        disabled={disabled}
      >
        <Clock size={16} className={styles.triggerIcon} />
        <span className={displayValue ? styles.triggerText : styles.triggerPlaceholder}>
          {displayValue || 'Select time'}
        </span>
        <ChevronDown size={16} className={`${styles.chevron} ${open ? styles.chevronOpen : ''}`} />
      </button>

      {open && (
        <>
          <div className={styles.backdrop} onClick={() => setOpen(false)} />
          <div className={styles.dropdown}>
            <div className={styles.slotsGrid}>
              {SLOTS.map((slot) => {
                const isDis = isSlotDisabled(slot.value);
                const isActive = value === slot.value;
                return (
                  <button
                    type="button"
                    key={slot.value}
                    className={`${styles.slot} ${isActive ? styles.slotActive : ''} ${isDis ? styles.slotDisabled : ''}`}
                    onClick={() => !isDis && handleSelect(slot.value)}
                    disabled={isDis}
                  >
                    {slot.label}
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default TimePicker12h;
